import { create } from "zustand";

type ModalRegistry = {
  // Report modals
  renameReport: { id: string; name: string };
  deleteReport: { id: string; action: string; parentReportId?: string | null };
  staleCheck: { isStale: boolean; parentReportId: string, reportId: string };
  saveAsNew: { id: string; name: string };
  groupReports: { groupId?: string; groupName: string; reportIds: string[] };
  ungroupReports: { reportIds: string[] };
  renameGroup: { id: string; name: string };
  deleteGroup: { id: string };

  // Group dashboard modals
  addSummary: { preselectedReportId?: string; reportGroupId?: string };
  renamePin: { pinId: string; displayName: string };
  removePin: { pinId: string };
};

type ModalState = {
  isOpen: boolean;
  data: any;
};

type ModalStore = {
  modals: Record<string, ModalState>;
  openModal: <K extends keyof ModalRegistry>(
    name: K,
    data?: ModalRegistry[K],
  ) => void;
  closeModal: (name: keyof ModalRegistry) => void;
  getModal: <K extends keyof ModalRegistry>(
    name: K,
  ) => { isOpen: boolean; data: ModalRegistry[K] };
};

export const useModalStore = create<ModalStore>((set, get) => ({
  modals: {},

  openModal: (name, data) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [name]: { isOpen: true, data: data ?? {} },
      },
    })),

  closeModal: (name) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [name]: { isOpen: false, data: {} },
      },
    })),

  getModal: (name) => {
    const modal = get().modals[name];
    return {
      isOpen: modal?.isOpen ?? false,
      data: (modal?.data ?? {}) as ModalRegistry[typeof name],
    };
  },
}));


export const useModal = <K extends keyof ModalRegistry>(name: K) => {
  const isOpen = useModalStore((s) => s.modals[name]?.isOpen ?? false);
  const data = useModalStore((s) => (s.modals[name]?.data ?? {}) as ModalRegistry[K]);


  return { isOpen, data };
}
